/**
 * Sections of a remote signing link (Monday 13411491362). The token stores the
 * rep's choice; the public report shows only those. This scopes what the page
 * renders, it is NOT a security boundary: the signing session can still read
 * the whole customer record with devtools. Pure and import-free: the backend
 * compiles it too (V62).
 */
export const SIGNING_SECTIONS = [
  'smart_home',
  'solar',
  'home_improvement',
  'roof_claim',
  'authorization',
  'cover',
  'certificate',
] as const
export type SigningSection = (typeof SIGNING_SECTIONS)[number]

export const SIGNING_SECTION_LABELS: Readonly<Record<SigningSection, string>> =
  {
    smart_home: 'Smart Home',
    solar: 'Solar',
    home_improvement: 'Home Improvements',
    roof_claim: 'Roof Claim',
    authorization: 'Authorization Form',
    cover: 'Cover & summary',
    certificate: 'Certificate of Completion',
  }

/**
 * Business defaults for remote links, proposed by the director on Nov 3 and
 * kept in one place so they are easy to change.
 */
export const REMOTE_SEND_RULES = {
  /** The rep cannot sign from the customer's link. */
  roofClaimNeedsRepSignature: true,
  /** The customer cannot type on the form; a blank would freeze blank (V99). */
  authorizationNeedsPolicyNumber: true,
  /** A sent authorization must be signed before Finish. */
  authorizationRequiredAtFinish: true,
} as const

/** Unknown value (a link from before sections) = null: legacy, full report. */
export function sanitizeSigningSections(
  value: unknown,
): SigningSection[] | null {
  if (!Array.isArray(value)) return null
  return SIGNING_SECTIONS.filter((section) => value.includes(section))
}

export function sectionSent(
  sections: readonly SigningSection[] | null,
  section: SigningSection,
): boolean {
  return !sections || sections.includes(section)
}

const PRODUCT_SECTION: Readonly<Record<string, SigningSection>> = {
  HIL: 'home_improvement',
  RC: 'roof_claim',
  Security: 'smart_home',
  Solar: 'solar',
}
/** Keeps `products.length === 0 means everything` from opening a scoped link. */
export const NO_PRODUCT_SENT = '__none__'

export function scopeProducts(
  products: readonly string[],
  sections: readonly SigningSection[] | null,
): readonly string[] {
  if (!sections) return products
  const deal = products.length ? products : Object.keys(PRODUCT_SECTION)
  const sent = deal.filter((product) => {
    const section = PRODUCT_SECTION[product]
    return !!section && sections.includes(section)
  })
  return sent.length ? sent : [NO_PRODUCT_SENT]
}

/** M+S, CE and ESR print their agreement whatever the products say. */
export function primaryContractSent(
  sections: readonly SigningSection[] | null,
): boolean {
  return (
    sectionSent(sections, 'smart_home') ||
    sectionSent(sections, 'home_improvement')
  )
}

export interface SectionChoiceInput {
  products: readonly string[]
  roofOnReport: boolean
  agreementAlwaysPrints: boolean
  includeRoofClaim: boolean
  includeAuthorization: boolean
  includeCover: boolean
  includeCertificate: boolean
}
export interface SectionChoice {
  available: SigningSection[]
  selected: SigningSection[]
}
/** Offered and preselected in the send dialog, from the deal and the menu. */
export function sectionChoice(input: SectionChoiceInput): SectionChoice {
  const { products, roofOnReport, agreementAlwaysPrints } = input
  const legacyAll = products.length === 0
  const has = (product: string) => legacyAll || products.includes(product)
  const agreementAlone =
    agreementAlwaysPrints && !has('Security') && !roofOnReport
  const rows: [SigningSection, boolean, boolean][] = [
    ['smart_home', has('Security'), has('Security')],
    ['solar', has('Solar'), has('Solar')],
    [
      'home_improvement',
      has('HIL') || agreementAlwaysPrints,
      has('HIL') || agreementAlone,
    ],
    ['roof_claim', roofOnReport, roofOnReport && input.includeRoofClaim],
    ['authorization', roofOnReport, roofOnReport && input.includeAuthorization],
    ['cover', true, input.includeCover],
    ['certificate', true, input.includeCertificate],
  ]
  return {
    available: rows.filter(([, offered]) => offered).map(([id]) => id),
    selected: rows.filter(([, , on]) => on).map(([id]) => id),
  }
}

const filled = (value: unknown): boolean =>
  typeof value === 'string' && !!value.trim()

export const SEND_NEEDS_SECTION = 'Choose at least one section to send'
export const SEND_NEEDS_REP_SIGNATURE =
  'Sign the Roof Claim Authorized Signature first: the rep cannot sign from the customer link'
export const SEND_NEEDS_POLICY_NUMBER =
  'Enter the Policy Number on the Authorization Form first: the customer cannot edit it'

/** Why this choice cannot be sent yet, or null when it can. */
export function remoteSendBlock(
  customer: Readonly<Record<string, unknown>>,
  sections: readonly SigningSection[],
): string | null {
  if (!sections.length) return SEND_NEEDS_SECTION
  if (
    REMOTE_SEND_RULES.roofClaimNeedsRepSignature &&
    sections.includes('roof_claim') &&
    !filled(customer['roof_claiming_company_rep_sign'])
  )
    return SEND_NEEDS_REP_SIGNATURE
  if (
    REMOTE_SEND_RULES.authorizationNeedsPolicyNumber &&
    sections.includes('authorization') &&
    !filled(customer['roof_third_party_sign']) &&
    !filled(customer['roof_third_party_policy_number'])
  )
    return SEND_NEEDS_POLICY_NUMBER
  return null
}

/** customerEmail first; a Roof Claim customer may only have the claim's own. */
export function remoteSigningRecipient(
  customer: Readonly<Record<string, unknown>>,
): string {
  for (const field of ['customerEmail', 'roof_claiming_email']) {
    const value = customer[field]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}
